import { useEffect, useRef, useState, type DragEvent } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { describeAccept } from '@shared/lib/fileAccept';
import { filesFromDrop, filesFromInput } from '@shared/lib/pickFiles';
import { expandArchives } from '@shared/lib/unzip';
import { IconFile } from './icons';

const iconBob = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-4px); }
`;

const Zone = styled.div<{ $active: boolean; $disabled: boolean; $compact: boolean }>`
  position: relative;
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.xl};
  padding: ${({ theme }) => theme.spacing(14)} ${({ theme }) => theme.spacing(8)};
  text-align: center;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;
  transition:
    border-color ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    background ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}80;
    background: ${({ theme }) => theme.colors.surface};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primarySoft};
      color: ${theme.colors.text.primary};
      transform: scale(1.01);
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;

      &:hover {
        border-color: inherit;
        background: inherit;
      }
    `}

  /* Компактный режим — для форм, где зона живёт в узкой колонке рядом с
     остальными полями (МТО, Экономист): иконка уезжает влево к тексту,
     высота падает втрое. Поведение то же. */
  ${({ $compact, theme }) =>
    $compact &&
    css`
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: ${theme.spacing(3)};
      padding: ${theme.spacing(4)};
      border-width: 1.5px;
      border-radius: ${theme.radius.md};
      text-align: left;
    `}
`;

/** Надпись зоны — настоящая кнопка: с клавиатуры (Tab, Enter/Space) выбор
 *  файлов открывается так же, как кликом (WCAG 2.1.1). Клик по ней всплывает в
 *  обработчик зоны — своего не нужно. «Выбрать папку» — соседняя кнопка, а не
 *  вложенная: содержимое role="button" скринридер не показывает. */
const PickButton = styled.button<{ $compact: boolean }>`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: none;
  font: inherit;
  color: inherit;
  text-align: inherit;
  cursor: inherit;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  /* Основа 240px: в узкой колонке «Выбрать папку целиком» уходит на
     следующую строку, а не сжимает надпись в столбик. */
  ${({ $compact, theme }) =>
    $compact &&
    css`
      display: flex;
      align-items: center;
      gap: ${theme.spacing(3)};
      flex: 1 1 240px;
      width: auto;
      min-width: 0;
    `}
`;

const Icon = styled.span<{ $active: boolean; $compact: boolean }>`
  width: 56px;
  height: 56px;
  margin: 0 auto ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $active }) =>
    $active &&
    css`
      animation: ${iconBob} 600ms ease-in-out infinite;
    `}

  ${({ $compact, theme }) =>
    $compact &&
    css`
      width: 36px;
      height: 36px;
      margin: 0;
      flex: 0 0 auto;
      border-radius: ${theme.radius.sm};
    `}
`;

/** В обычном режиме `display: contents` — заголовок и подпись остаются
 *  прямыми детьми зоны, вёрстка та же, что была. В компактном — собирает их
 *  в колонку справа от иконки. */
const Texts = styled.span<{ $compact: boolean }>`
  display: contents;

  ${({ $compact }) =>
    $compact &&
    css`
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      min-width: 0;
      flex: 1;
    `}
`;

const Primary = styled.span<{ $compact: boolean }>`
  display: block;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};

  ${({ $compact }) =>
    $compact &&
    css`
      font-size: 13px;
      margin-bottom: 0;
    `}
`;

const Hint = styled.span<{ $compact: boolean }>`
  display: block;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  ${({ $compact }) =>
    $compact &&
    css`
      font-size: 12px;
    `}
`;

const Note = styled.div<{ $compact: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.status.warning};

  ${({ $compact }) =>
    $compact &&
    css`
      flex-basis: 100%;
      margin-top: 0;
    `}
`;

const PickDirButton = styled.button<{ $compact: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};

  ${({ $compact }) =>
    $compact &&
    css`
      margin-top: 0;
      flex: 0 0 auto;
    `}

  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

interface BaseProps {
  /** Фильтр file-picker (`<input accept>`). `ACCEPT_ANY` (пустая строка) =
   *  без фильтра, атрибут не рендерится и диалог открывается на «Все файлы».
   *
   *  Обязателен намеренно: пока здесь стоял молчаливый дефолт, его неумышленно
   *  унаследовали пять форм юр-агента, и юристы вручную переключали диалог с
   *  «Пользовательские файлы» на «Все файлы». Значения — в `@shared/lib/fileAccept`. */
  accept: string;
  hint?: string;
  /** Заголовок зоны. По умолчанию собирается из `accept`; переопределяется,
   *  когда нужна формулировка под конкретный сценарий. */
  primary?: string;
  /** Зона не принимает файлы: ни клик, ни бросок. Для «занято» (идёт разбор
   *  предыдущей пачки) — текст состояния передавайте через `primary`. Права на
   *  запись лучше показывать не этим, а подписью вместо зоны: серая зона без
   *  объяснения читается как поломка. */
  disabled?: boolean;
  /** Компактная раскладка: иконка в строку с текстом, высота втрое меньше.
   *  Для форм, где зона стоит в узкой колонке среди других полей (МТО,
   *  Экономист), а не занимает отдельный экран/модалку, как в юр-агенте. */
  compact?: boolean;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  onFile: (file: File) => void;
}

interface MultiProps extends BaseProps {
  multiple: true;
  onFiles: (files: File[]) => void;
}

type Props = SingleProps | MultiProps;

export function FileDropZone(props: Props) {
  const { accept, hint, primary, multiple, disabled = false, compact = false } = props;
  const [active, setActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [notes, setNotes] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dirInputRef = useRef<HTMLInputElement>(null);

  // webkitdirectory/directory нет в типах React, а ставить их через каст пропсов
  // грязнее, чем один раз проставить атрибутами.
  useEffect(() => {
    const el = dirInputRef.current;
    if (!el) return;
    el.setAttribute('webkitdirectory', '');
    el.setAttribute('directory', '');
  }, []);

  /**
   * Папка, архив и просто файлы сходятся здесь в один плоский список — дальше
   * форма не различает, как именно юрист выбрал комплект.
   */
  async function emit(picked: File[]) {
    if (picked.length === 0) return;
    if (!multiple) {
      const first = picked[0];
      if (first) (props as SingleProps).onFile(first);
      return;
    }
    // Обход папки и распаковка на сотнях файлов не мгновенны — показываем, что
    // зона не зависла.
    setScanning(true);
    try {
      const { files, warnings } = await expandArchives(picked, accept);
      setNotes(warnings);
      if (files.length > 0) (props as MultiProps).onFiles(files);
    } finally {
      setScanning(false);
    }
  }

  async function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setActive(false);
    if (disabled) return;
    setScanning(true);
    try {
      await emit(await filesFromDrop(e.dataTransfer, accept));
    } finally {
      setScanning(false);
    }
  }

  return (
    <Zone
      $active={active}
      $disabled={disabled}
      $compact={compact}
      onClick={() => {
        if (!disabled) inputRef.current?.click();
      }}
      onDragOver={(e) => {
        // preventDefault обязателен и на заблокированной зоне: без него браузер
        // откроет брошенный файл во вкладке поверх приложения.
        e.preventDefault();
        if (!disabled) setActive(true);
      }}
      onDragLeave={() => setActive(false)}
      onDrop={handleDrop}
    >
      <PickButton type="button" disabled={disabled} $compact={compact}>
        <Icon $active={active} $compact={compact}>
          <IconFile size={compact ? 18 : 28} />
        </Icon>
        <Texts $compact={compact}>
          <Primary $compact={compact}>
            {scanning
              ? 'Читаем выбранное…'
              : (primary ??
                (multiple
                  ? `Перетащите ${describeAccept(accept)}, папку или архив`
                  : `Перетащите ${describeAccept(accept)}`))}
          </Primary>
          <Hint $compact={compact}>{hint ?? 'или нажмите, чтобы выбрать файл'}</Hint>
        </Texts>
      </PickButton>
      {notes.map((note) => (
        <Note key={note} $compact={compact}>
          {note}
        </Note>
      ))}
      {multiple && (
        <PickDirButton
          type="button"
          disabled={disabled}
          $compact={compact}
          onClick={(e) => {
            // Клик по зоне открывает выбор файлов — кнопке нужен свой диалог.
            e.stopPropagation();
            dirInputRef.current?.click();
          }}
        >
          Выбрать папку целиком
        </PickDirButton>
      )}
      <input
        ref={inputRef}
        type="file"
        // Пустой accept = без фильтра: не рендерим атрибут вовсе, иначе браузер
        // покажет «нет подходящих файлов». Непустой — фильтруем как раньше.
        accept={accept || undefined}
        multiple={multiple}
        hidden
        onChange={(e) => {
          void emit(filesFromInput(e.target.files, accept));
          e.target.value = '';
        }}
      />
      {multiple && (
        <input
          ref={dirInputRef}
          type="file"
          // accept с webkitdirectory не сочетается: Chrome тогда прячет папки в
          // диалоге. Отбираем нужное сами, уже после выбора.
          hidden
          onChange={(e) => {
            emit(filesFromInput(e.target.files, accept));
            e.target.value = '';
          }}
        />
      )}
    </Zone>
  );
}
